// @flow
import useHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import * as React from 'react';
import { withLeaflet } from 'react-leaflet';
import { withRouter } from 'react-router';
import type { LatLng, LeafletContext } from 'react-leaflet/lib/types';
import { Box } from 'grommet';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import { FormattedMessage, IntlShape, injectIntl } from 'react-intl';
import GisLineIcon from 'esm-web-pro/components/layout/sidePanel/icons/GisLineIcon';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import type { VersionableModel } from 'esm-web-pro/types/common';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import GisLineContent from './GisLineContent';
import GisLineActions from './GisLineActions';
import GisLineAttributes from './GisLineAttributes';

export type GISLine = {
    ...VersionableModel,
    uuid: string,
    path: {
        coordinates: Array<LatLng>
    },
    air_route_cables: Array<Object>,
    pipes: Array<{
        cables: Object,
        is_virtual: boolean,
        count: number,
    }>,
    start_point: Object,
    end_point: Object,
    ref: string,
}

type Props = {
    panelId: String,
    object: GISLine,
    leaflet: LeafletContext,
    map: { is_draft: boolean, uuid: string },
    history: Object,
    match: Object,
    editor: BaseEditor,
    intl: IntlShape,
    updateObject: (data: Object) => void,
}

const GisLine = (props: Props) => {
    const {
        object: line, leaflet: { map },
        map: networkMap, editor, updateObject, intl: { formatMessage }, panelId,
    } = props;

    const [clicked, setClicked] = React.useState(null);

    const { start_point: startPoint, end_point: endPoint } = line;
    const startPointCoords = startPoint?.coordinates?.coordinates;
    const endPointCoords = endPoint?.coordinates?.coordinates;

    const highlightGisLine = React.useCallback(useHighlightLine(
        line.path.coordinates.map((c) => [c[1], c[0]]),
        [{ dashArray: '20', className: 'route-line' },
            { dashArray: line.cables_count > 0 ? null : [10, 5], className: '' }],
        null,
    ), [line]);

    let startNumber = startPoint.sequence;
    if (startPoint?.point?.type === 'pop' && startPoint.alias) {
        startNumber = startPoint.alias;
    }
    let endNumber = endPoint.sequence;
    if (endPoint?.point?.type === 'pop' && endPoint.alias) {
        endNumber = endPoint.alias;
    }
    const startPointCallback = React.useCallback(
        UseHighlightPoint(startPointCoords, startNumber), [startPoint],
    );
    const endPointCallback = React.useCallback(
        UseHighlightPoint(endPointCoords, endNumber), [endPoint],
    );

    const handleView = React.useCallback(() => {
        const lineCoords = line.path.coordinates.map((c) => [c[1], c[0]]);
        editor.unregisterAllLoadingCallback();
        editor.registerLoadingCallback(startPointCallback);
        editor.registerLoadingCallback(endPointCallback);
        editor.registerLoadingCallback(highlightGisLine);
        if (map) {
            map.highlightedGeometry = {
                bounds: lineCoords, center: null,
            };
            map.panInsideBounds(lineCoords);
        }
        editor.reloadFeatures();
    }, [map, line, editor, startPointCallback, endPointCallback, highlightGisLine]);

    React.useEffect(() => {
        handleView();
        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [handleView]);

    const navigateBack = () => {
        props.history.push(props.match.url.split('/layer')[0]);
    };

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={line.ref}
            titleLabel={<FormattedMessage id="gisline-panel-title" defaultMessage="Route" />}
            titleIcon={<GisLineIcon />}
            titleOnView={handleView}
            actions={<NavigateBackButton panelId={panelId} />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <GisLineAttributes line={line} url={props.match.url} onView={handleView} />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <GisLineActions
                                line={line}
                                map={networkMap}
                                navigateBack={navigateBack}
                                editor={editor}
                                updateObject={updateObject}
                                onView={handleView}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                    <BottomContainer>
                        <GisLineContent
                            line={line}
                            editor={editor}
                            clicked={clicked}
                            setClicked={setClicked}
                            map={networkMap}
                        />
                    </BottomContainer>
                </Box>
            </Box>
        </SidePane>
    );
};

export default withRouter(withLeaflet(injectIntl(React.memo<Props>(GisLine))));
